import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { usePluginStore } from '@/state/usePluginStore';
import { summarizeViewshed } from './viewshedPlugin';

interface ViewshedPanelProps {
  readonly annotations: ReadonlyArray<AnnotationEntry>;
}

export function ViewshedPanel({ annotations }: ViewshedPanelProps) {
  const { 
    engineType, 
    engineReady, 
    isPluginAvailable, 
    getPluginState, 
    transient, 
    updateViewshedTransient,
    setPluginState 
  } = usePluginStore();
  
  const available = isPluginAvailable('viewshed');
  const state = getPluginState('viewshed');
  const summary = summarizeViewshed(annotations);

  const handleComputeViewshed = async () => {
    if (!transient.viewshed.observerPoint) return;
    
    setPluginState('viewshed', 'computing');
    
    // Simulate API call - in real implementation this would call BE-123
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setPluginState('viewshed', 'idle');
    } catch {
      setPluginState('viewshed', 'error');
    }
  };

  if (!available) {
    return (
      <div className="plugin-not-available">
        <div className="plugin-status-banner">
          <span className="material-symbols-outlined">warning</span>
          <div>
            <strong>Viewshed not available</strong>
            <p>
              {!engineType && 'Map engine not initialized'}
              {engineType && !engineReady && `${engineType} engine loading...`}
              {engineType && engineReady && `${engineType} engine does not support viewshed analysis`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="plugin-engine-info">
        <span className="engine-badge">
          <span className="material-symbols-outlined">
            {engineType === 'maplibre' ? 'map' : engineType === 'cesium' ? 'language' : 'layers'}
          </span>
          {engineType} engine
        </span>
        {state === 'computing' && (
          <span className="computing-indicator">
            <span className="material-symbols-outlined">hourglass_empty</span>
            Computing viewshed...
          </span>
        )}
      </div>

      <p>Viewshed analysis computes visibility from an observer point using {engineType} engine terrain data.</p>

      <div className="viewshed-parameters">
        <h4>Observer Parameters</h4>
        
        <div className="parameter-group">
          <label htmlFor="observer-height">
            <span className="material-symbols-outlined">height</span>
            Observer Height (m)
          </label>
          <input
            id="observer-height"
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={transient.viewshed.height}
            onChange={(e) => updateViewshedTransient({ height: parseFloat(e.target.value) })}
            disabled={state === 'computing'}
          />
        </div>

        <div className="parameter-group">
          <label htmlFor="max-distance">
            <span className="material-symbols-outlined">straighten</span>
            Max Distance (m)
          </label>
          <input
            id="max-distance"
            type="number"
            min="100"
            max="10000"
            step="100"
            value={transient.viewshed.maxDistance}
            onChange={(e) => updateViewshedTransient({ maxDistance: parseInt(e.target.value) })}
            disabled={state === 'computing'}
          />
        </div>

        <div className="parameter-group">
          <label htmlFor="field-of-view">
            <span className="material-symbols-outlined">visibility</span>
            Field of View (degrees)
          </label>
          <input
            id="field-of-view"
            type="number"
            min="30"
            max="360"
            step="15"
            value={transient.viewshed.fov}
            onChange={(e) => updateViewshedTransient({ fov: parseInt(e.target.value) })}
            disabled={state === 'computing'}
          />
        </div>
      </div>

      <div className="plugin-actions">
        <button
          type="button"
          className="primary"
          onClick={handleComputeViewshed}
          disabled={!transient.viewshed.observerPoint || state === 'computing'}
        >
          <span className="material-symbols-outlined">
            {state === 'computing' ? 'hourglass_empty' : 'radar'}
          </span>
          {state === 'computing' ? 'Computing...' : 'Compute Viewshed'}
        </button>
      </div>

      {!transient.viewshed.observerPoint && (
        <div className="plugin-help">
          Click on the map to place an observer point, then adjust parameters and compute.
        </div>
      )}

      <dl className="plugin-measurements" aria-label="Viewshed summary">
        <div>
          <dt>
            <span className="material-symbols-outlined">info</span>
            Status
          </dt>
          <dd>{summary.status}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">place</span>
            Observer
          </dt>
          <dd>{summary.observerLabel}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">settings</span>
            Strategy
          </dt>
          <dd>{summary.engineStrategy}</dd>
        </div>
      </dl>

      {state === 'error' && (
        <div className="plugin-error-banner">
          <span className="material-symbols-outlined">error</span>
          <div>
            <strong>Viewshed computation failed</strong>
            <p>Try adjusting parameters or check terrain data availability.</p>
          </div>
        </div>
      )}
    </>
  );
}
